/**
 * Active-time tracker — counts seconds while the estimator is actually
 * working anywhere in the Estimate module (Home included):
 * - activity = pointer move/down, keys, wheel, touch, scroll
 * - pauses after 300 s (5 full minutes) with no activity — the grace period
 *   counts; the paused gap after it never does
 * - pauses immediately on tab hide / window blur, resumes on return;
 *   a hidden tab never accrues, so two tabs of the same browser can't
 *   double-count
 * - flushes to the backend ~every 15 s and on teardown
 * - unflushed seconds persist in localStorage and are restored + flushed
 *   on the next visit, so a refresh never loses time
 * - a failed flush is kept (not dropped) and retried on the next flush
 * - onTick({seconds, running}) fires every tick so a visible timer pill can
 *   show the live count and Running/Paused state
 */
import { useEffect, useRef } from "react";
import { addTime } from "./api";

const IDLE_MS = 300_000;
const FLUSH_MS = 15_000;
const EVENTS = ["pointermove", "pointerdown", "keydown", "wheel", "touchstart", "scroll"];

const pendingKey = (projectId) => `takeoff-time-pending-${projectId}`;

// Live tick feed for the visible timer pill. Subscribing renders the pill
// only — the module shell doesn't re-render every second.
const listeners = new Set();
export const onTimeTick = (fn) => { listeners.add(fn); return () => listeners.delete(fn); };
const emit = (t) => listeners.forEach((fn) => { try { fn(t); } catch { /* listener fault */ } });

export function useActiveTime(projectId, enabled) {
  const secondsRef = useRef(0);
  const lastActivityRef = useRef(Date.now());
  const activeRef = useRef(false);

  useEffect(() => {
    if (!projectId || !enabled) return undefined;
    const storeKey = pendingKey(projectId);
    // restore seconds that never made it to the server (refresh / crash)
    try { secondsRef.current += Number(localStorage.getItem(storeKey)) || 0; } catch { /* ignore */ }
    const persist = () => {
      try {
        const s = Math.floor(secondsRef.current);
        if (s > 0) localStorage.setItem(storeKey, String(s));
        else localStorage.removeItem(storeKey);
      } catch { /* ignore */ }
    };
    const markActivity = () => {
      if (document.hidden) return;
      lastActivityRef.current = Date.now();
      activeRef.current = true;
    };
    const onVis = () => {
      if (document.hidden) activeRef.current = false;
      else markActivity();
    };
    const onBlur = () => { activeRef.current = false; };
    const onFocus = () => markActivity();
    markActivity();

    const tick = setInterval(() => {
      if (!activeRef.current || document.hidden) {
        emit({ seconds: secondsRef.current, running: false, projectId });
        return;
      }
      if (Date.now() - lastActivityRef.current > IDLE_MS) {
        activeRef.current = false;
        emit({ seconds: secondsRef.current, running: false, projectId });
        return;
      }
      secondsRef.current += 1;
      emit({ seconds: secondsRef.current, running: true, projectId });
    }, 1000);

    const flush = () => {
      const s = Math.floor(secondsRef.current);
      if (s > 0 && projectId) {
        secondsRef.current -= s;
        addTime(projectId, s)
          .then(() => { try { localStorage.removeItem(storeKey); } catch { /* ignore */ } })
          .catch(() => { secondsRef.current += s; persist(); });
      }
    };
    flush(); // push anything restored from a previous session
    const flusher = setInterval(flush, FLUSH_MS);

    for (const ev of EVENTS) window.addEventListener(ev, markActivity, { passive: true, capture: true });
    document.addEventListener("visibilitychange", onVis);
    window.addEventListener("blur", onBlur);
    window.addEventListener("focus", onFocus);
    window.addEventListener("pagehide", persist);
    return () => {
      clearInterval(tick);
      clearInterval(flusher);
      for (const ev of EVENTS) window.removeEventListener(ev, markActivity, { capture: true });
      document.removeEventListener("visibilitychange", onVis);
      window.removeEventListener("blur", onBlur);
      window.removeEventListener("focus", onFocus);
      window.removeEventListener("pagehide", persist);
      persist(); // keep unflushed seconds safe across refresh
      flush();
    };
  }, [projectId, enabled]);
}

export const fmtDuration = (secs) => {
  const s = Math.floor(Number(secs) || 0);
  const h = Math.floor(s / 3600), m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${String(m).padStart(2, "0")}m`;
  if (m) return `${m}m ${String(s % 60).padStart(2, "0")}s`;
  return `${s}s`;
};
